/**
 * Vehicle detail fields for the Vehicle Request tab: condition / certified /
 * primary radios, then Year, Make, Model, colors, Trim, Body style, Fuel type,
 * Transmission. All of these accept a free-typed value as well as a selection:
 *   - Make/Model use AutoComplete (backed by `GET_VEHICLE_MAKES` /
 *     `GET_VEHICLE_MODELS`) so whatever the user types becomes the value even
 *     when the backend returns no matching list — important because Model is
 *     required and some make/year combos have no models on file.
 *   - Year/Body style are searchable Selects whose typed term is injected as a
 *     selectable option.
 * The current form value is always kept in the suggestion list (e.g. a make
 * returned by VIN decode that isn't in the first page of results).
 *
 * @module vehicleModal/components/VehicleDetailsForm
 */
import { useQuery } from "@apollo/client";
import { AutoComplete, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { GET_VEHICLE_MAKES, GET_VEHICLE_MODELS } from "../../../../graphql/query";
import { BODY_STYLE_OPTIONS } from "../constants";
import { requiredRule, yearRules } from "../utils/vehicleValidation";

const YEARS = Array.from({ length: 30 }, (_, i) => dayjs().year() + 1 - i);
const YEAR_OPTIONS = YEARS.map(y => ({ value: String(y), label: String(y) }));
const BODY_STYLE_BASE_OPTIONS = BODY_STYLE_OPTIONS.map(b => ({ value: b, label: b }));

/**
 * Build antd `options` from a list of strings, ensuring the current value is
 * present even if the query didn't return it (e.g. a make from VIN decode that
 * isn't in the first page of results).
 * @param {string[]} values
 * @param {string} [current]
 * @returns {{value: string, label: string}[]}
 */
function withCurrentValue(values, current) {
  const set = new Set(values.filter(Boolean));
  if (current && !set.has(current)) set.add(current);
  return Array.from(set).map(v => ({ value: v, label: v }));
}

/**
 * Prepend the user's typed search term as a selectable option so a custom value
 * (not present in the list / not returned by the backend) can be entered. The
 * term is skipped when it already matches an existing option (case-insensitive).
 * @param {{value: string, label: string}[]} options
 * @param {string} [search]
 * @returns {{value: string, label: string}[]}
 */
function withTypedOption(options, search) {
  const term = (search || "").trim();
  if (!term) return options;
  const exists = options.some(o => String(o.value).toLowerCase() === term.toLowerCase());
  return exists ? options : [{ value: term, label: term }, ...options];
}

/**
 * @param {Object} props
 * @param {import("antd").FormInstance} props.form
 * @param {boolean} [props.requireCore] - require Year/Make/Model (manual entry)
 */
export default function VehicleDetailsForm({ form, requireCore = false }) {
  const make = Form.useWatch("make", form);
  const year = Form.useWatch("year", form);
  const currentModel = Form.useWatch("model", form);

  const [makeSearch, setMakeSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [yearSearch, setYearSearch] = useState("");
  const [bodyStyleSearch, setBodyStyleSearch] = useState("");

  const { data: makesData } = useQuery(GET_VEHICLE_MAKES, {
    variables: { search: makeSearch },
    fetchPolicy: "cache-first",
  });
  const { data: modelsData } = useQuery(GET_VEHICLE_MODELS, {
    variables: { makeName: make, year: year ? Number(year) : undefined, search: modelSearch },
    skip: !make || !year,
    fetchPolicy: "cache-first",
  });

  // Make/Model use AutoComplete (free text + suggestions), so the typed value
  // commits on its own — the option lists are just suggestions.
  const makeOptions = useMemo(
    () =>
      withCurrentValue(
        (makesData?.getVehicleMakes?.data || []).map(m => m.MakeName),
        make
      ),
    [makesData, make]
  );
  const modelOptions = useMemo(
    () =>
      withCurrentValue(
        (modelsData?.getVehicleModels?.data || []).map(m => m.ModelName),
        currentModel
      ),
    [modelsData, currentModel]
  );
  const yearOptions = useMemo(() => withTypedOption(YEAR_OPTIONS, yearSearch), [yearSearch]);
  const bodyStyleOptions = useMemo(
    () => withTypedOption(BODY_STYLE_BASE_OPTIONS, bodyStyleSearch),
    [bodyStyleSearch]
  );

  return (
    <div className="flex flex-col gap-1">
      {/* Year / Make / Model */}
      <div className="flex flex-row gap-3">
        <Form.Item
          name="year"
          label="Year"
          className="w-1/3"
          rules={yearRules({ required: requireCore })}
        >
          <Select
            showSearch
            allowClear
            placeholder="Year"
            options={yearOptions}
            onSearch={setYearSearch}
            onChange={() => {
              // Year drives the model list; a user year change can invalidate
              // the chosen model, so clear it. (VIN auto-fill uses
              // setFieldsValue, which does not fire this onChange.)
              form.setFieldsValue({ model: undefined });
            }}
          />
        </Form.Item>
        <Form.Item
          name="make"
          label="Make"
          className="w-1/3"
          rules={requireCore ? requiredRule("Please enter Make") : undefined}
        >
          <AutoComplete
            allowClear
            placeholder="Make"
            filterOption={false}
            options={makeOptions}
            onSearch={setMakeSearch}
            onChange={() => form.setFieldsValue({ model: undefined })}
            disabled={!year}
          />
        </Form.Item>
        <Form.Item
          name="model"
          label="Model"
          className="w-1/3"
          rules={requireCore ? requiredRule("Please enter Model") : undefined}
        >
          <AutoComplete
            allowClear
            placeholder="Model"
            filterOption={false}
            options={modelOptions}
            onSearch={setModelSearch}
            disabled={!make || !year}
          />
        </Form.Item>
      </div>

      {/* Exterior / Interior color / Trim */}
      <div className="flex flex-row gap-3">
        <Form.Item name="exteriorColor" label="Exterior Color" className="w-1/3">
          <Input placeholder="Exterior Color" />
        </Form.Item>
        <Form.Item name="interiorColor" label="Interior Color" className="w-1/3">
          <Input placeholder="Interior Color" />
        </Form.Item>
        <Form.Item name="trim" label="Trim" className="w-1/3">
          <Input placeholder="Trim" />
        </Form.Item>
      </div>

      {/* Body style / Fuel type / Transmission */}
      <div className="flex flex-row gap-3">
        <Form.Item name="bodyStyle" label="Body style" className="w-1/3">
          <Select
            showSearch
            allowClear
            placeholder="Body style"
            options={bodyStyleOptions}
            onSearch={setBodyStyleSearch}
          />
        </Form.Item>
        <Form.Item name="fuelType" label="Fuel type" className="w-1/3">
          <Input placeholder="Fuel type" />
        </Form.Item>
        <Form.Item name="transmission" label="Transmission" className="w-1/3">
          <Input placeholder="Transmission" />
        </Form.Item>
      </div>
    </div>
  );
}
